import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import Link from "next/link";
import { useRouter } from "next/router";
import { BiInfoCircle } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { MAX_BOOKING_MONTHS_IN_ADVANCE } from "../../../../../core/availability";
import {
  Product,
  ProductAvailabilityType,
} from "../../../../../graphql/operations";
import useAvailability from "../../../../../hooks/useAvailability";
import { formatDate } from "../../../../../utils/calendarUtils";
import Button from "../../../../Atoms/Button";
import HurryUpMessage from "../../../../Cards/HurryUpMessage";
import ReservationDatepicker, {
  ReservationDatepickerProps,
} from "../../../../Cards/ReservationDatepicker";
import HowThisWorks from "../../../Checkout/Parts/HowThisWorks";
import WaitingListButton from "../ProductWaitingListButton";
import styles from "./ProductCalendar.module.scss";

export type ProductCalendarProps = {
  countryName: string;
  productId: string;
  children: ReactNode;
  inlineCalendar?: boolean;
  productOptions?: any;
  setIsAvailable: Function;
  initalAvailability: ReturnType<typeof useAvailability>;
} & Pick<ReservationDatepickerProps, "isPrivate" | "toggleIsPrivate"> &
  Pick<Product, "availabilityType" | "privatePrices" | "location">;

const BOOKING_SIZE = 1;

export default function ProductCalendar({
  productId,
  children,
  inlineCalendar,
  productOptions,
  isPrivate,
  toggleIsPrivate,
  availabilityType,
  initalAvailability,
  setIsAvailable,
  privatePrices,
  countryName,
}: ProductCalendarProps) {
  const router = useRouter();
  const { query } = router;
  const queryDate = query?.date as unknown as Date;

  const availability = useAvailability({
    productId: productId,
    isPrivate,
    duration: { months: MAX_BOOKING_MONTHS_IN_ADVANCE },
    preloadedAvailability: initalAvailability.availabilities,
  });
  const [date, setDate] = useState<Date | null>(queryDate);
  const [time, setTime] = useState<string | null>(null);
  const {
    error,
    isAvailableInYear,
    isAvailableInRange,
    availableDates,
    isTimeSlotAvailable,
  } = availability;

  useEffect(() => {
    if (!date && !time && availableDates.length) {
      const initialAvailability = availableDates[0];
      const initialDate = initialAvailability.date;
      const initialTime = initialAvailability.timeSlots.find((timeSlot) =>
        isTimeSlotAvailable(initialDate, timeSlot.time)
      ).time;
      setDate(initialDate);
      setTime(initialTime);
    }
  }, [date, time, availableDates, isTimeSlotAvailable]);

  useEffect(() => {
    setIsAvailable(isAvailableInYear);
  }, [isAvailableInYear, setIsAvailable]);

  const [showLoader, setShowLoader] = useState(false);

  const [openDis, setOpenDis] = useState(false);
  const onOpenDisModal = () => setOpenDis(true);
  const onCloseDisModal = () => setOpenDis(false);
  const modalRef = useRef(null);

  const privateParameter = isPrivate ? `&isprivate=true` : "";
  const timeParameter = time ? `&time=${time}` : "";
  const url =
    `/checkout/${productId}?date=${formatDate(date)}` +
    timeParameter +
    privateParameter;

  const UnavailableTourMessageContainer = useCallback(
    ({ children }: React.PropsWithChildren) => {
      return productId && productOptions ? (
        <WaitingListButton
          productId={productId}
          productOptions={productOptions}
        >
          {children}
        </WaitingListButton>
      ) : (
        <p style={{ textAlign: "center" }}>{children}</p>
      );
    },
    [productId, productOptions]
  );

  if (error) return <p>{error.message}</p>;

  return (
    <div className={styles.Container}>
      {isAvailableInYear ? (
        <>
          <HurryUpMessage isAvailableInRange={isAvailableInRange} />
          <ReservationDatepicker
            countryName={countryName}
            {...availability}
            isPrivate={isPrivate}
            toggleIsPrivate={toggleIsPrivate}
            inlineCalendar={inlineCalendar}
            date={date}
            time={time}
            setDate={setDate}
            setTime={setTime}
            bookingSize={BOOKING_SIZE}
            canToggleGroupPrivacy={
              availabilityType === ProductAvailabilityType.PublicAndPrivate &&
              !!privatePrices.find(
                (privatePrice) => privatePrice.price !== null
              )
            }
          />
          {children && <div className={styles.box}>{children}</div>}
          <Link href={url} onClick={() => setShowLoader(true)}>
            <Button large>
              {showLoader ? (
                <TailSpin
                  color="#FFFFFF"
                  height={18}
                  width={18}
                  ariaLabel="tail-spin-loading"
                />
              ) : (
                "Book Now"
              )}
            </Button>
          </Link>
          <div className={styles.HowThisWorksLink} onClick={onOpenDisModal}>
            <BiInfoCircle
              color="#D81B60"
              size={16}
              style={{ position: "relative", top: 3 }}
            />{" "}
            <span
              style={{
                color: "#D81B60",
                textDecoration: "underline",
                fontSize: 14,
              }}
            >
              How it works
            </span>
          </div>
          {productId && productOptions && (
            <>
              <WaitingListButton
                productId={productId}
                productOptions={productOptions}
              />
            </>
          )}
        </>
      ) : (
        <UnavailableTourMessageContainer>
          Currently not available
        </UnavailableTourMessageContainer>
      )}
      <Modal
        open={openDis}
        blockScroll={false}
        onClose={onCloseDisModal}
        ref={modalRef}
        initialFocusRef={modalRef}
        classNames={{
          modal: styles.CustomModal2,
        }}
      >
        <HowThisWorks />
      </Modal>
    </div>
  );
}
